export type JourneyMapCompletionSource =
  | 'manual'
  | 'provider_api'
  | 'manager'
  | 'system'
  | 'assessment'
  | 'unknown';

export type JourneyMapCompletionStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'approved'
  | 'failed'
  | 'blocked'
  | 'pending_approval';

export type CompletionMode = 'manual' | 'manager' | 'auto_complete';
export type AutoCompleteType = 'course_completion' | 'assessment_score';
export type ResponseProcess = 'numeric_gte' | 'boolean_true' | 'status_match';

export type JourneyMapCompletionProviderConfig = {
  courseCheckEndpoint: string;
  assessmentCheckEndpoint: string;
  referenceField: string;
  responseAttribute: string;
  responseProcess: ResponseProcess;
  passStatuses: string[];
  emailParam: string;
  referenceParam: string;
};

export type JourneyMapCompletionConfigValue = {
  mode: CompletionMode;
  autoCompleteType: AutoCompleteType | null;
  thresholdPercent: number | null;
  providerConfig: JourneyMapCompletionProviderConfig;
};

export type LegacyJourneyMapCompletionConfig = {
  allowManualCompletion: boolean;
  requireManagerApproval: boolean;
  allowManagerApproval: boolean;
  autoCompleteFromProvider: boolean;
  passingScore: number | null;
  sourcePriority: JourneyMapCompletionSource[];
  providerMappings: {
    statusField?: string;
    completedAtField?: string;
    progressField?: string;
    scoreField?: string;
    attemptsField?: string;
    approvedAtField?: string;
    approvedByField?: string;
  };
  statusMapping: Record<string, JourneyMapCompletionStatus>;
};

export type NormalizedCompletionRecord = {
  source: JourneyMapCompletionSource;
  status: JourneyMapCompletionStatus;
  progressPercent: number;
  completedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  score: number | null;
  attemptCount: number | null;
  isManual: boolean;
  isProviderManaged: boolean;
  raw: any;
};

export type ResolvedJourneyMapCompletion = {
  finalStatus: JourneyMapCompletionStatus;
  progressPercent: number;
  winningSource: JourneyMapCompletionSource | null;
  completedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  score: number | null;
  attemptCount: number | null;
  records: NormalizedCompletionRecord[];
  canManuallyComplete: boolean;
  canRequestApproval: boolean;
  canApprove: boolean;
};

export const DEFAULT_LEGACY_JOURNEY_MAP_COMPLETION_CONFIG: LegacyJourneyMapCompletionConfig = {
  allowManualCompletion: true,
  requireManagerApproval: false,
  allowManagerApproval: true,
  autoCompleteFromProvider: true,
  passingScore: null,
  sourcePriority: ['manager', 'provider_api', 'assessment', 'manual', 'system', 'unknown'],
  providerMappings: {
    statusField: 'status',
    completedAtField: 'completed_at',
    progressField: 'progress',
    scoreField: 'score',
    attemptsField: 'attempts',
    approvedAtField: 'approved_at',
    approvedByField: 'approved_by',
  },
  statusMapping: {
    completed: 'completed',
    complete: 'completed',
    passed: 'completed',
    approved: 'approved',
    failed: 'failed',
    blocked: 'blocked',
    pending_approval: 'pending_approval',
    pending: 'pending_approval',
    in_progress: 'in_progress',
    started: 'in_progress',
    not_started: 'not_started',
  },
};

export const DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG: JourneyMapCompletionConfigValue = {
  mode: 'manual',
  autoCompleteType: null,
  thresholdPercent: null,
  providerConfig: {
    courseCheckEndpoint: '',
    assessmentCheckEndpoint: '',
    referenceField: 'content_ref_id',
    responseAttribute: '',
    responseProcess: 'numeric_gte',
    passStatuses: ['completed', 'passed'],
    emailParam: 'email',
    referenceParam: 'course_id',
  },
};

function asNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function asString(value: any): string | null {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

function asBoolean(value: any): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lowered = value.trim().toLowerCase();
    return lowered === 'true' || lowered === '1' || lowered === 'yes';
  }
  if (typeof value === 'number') return value === 1;
  return false;
}

function clampPercent(value: any): number {
  const parsed = asNumber(value);
  if (parsed === null) return 0;
  if (parsed < 0) return 0;
  if (parsed > 100) return 100;
  return parsed;
}

function getByPath(obj: any, path?: string): any {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function normalizeSource(value: any): JourneyMapCompletionSource {
  const text = String(value || '').toLowerCase();

  if (text.includes('manual')) return 'manual';
  if (text.includes('provider') || text.includes('api') || text.includes('lms')) {
    return 'provider_api';
  }
  if (text.includes('manager') || text.includes('approval')) return 'manager';
  if (text.includes('assessment') || text.includes('quiz') || text.includes('test')) {
    return 'assessment';
  }
  if (text.includes('system')) return 'system';

  return 'unknown';
}

function normalizeStatus(
  rawStatus: any,
  statusMapping: Record<string, JourneyMapCompletionStatus>,
  fallback?: JourneyMapCompletionStatus,
): JourneyMapCompletionStatus {
  const normalized = String(rawStatus || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

  if (normalized && statusMapping[normalized]) {
    return statusMapping[normalized];
  }

  if (
    normalized === 'complete' ||
    normalized === 'completed' ||
    normalized === 'passed'
  ) {
    return 'completed';
  }

  if (normalized === 'approved') return 'approved';
  if (normalized === 'failed') return 'failed';
  if (normalized === 'blocked') return 'blocked';
  if (normalized === 'pending_approval' || normalized === 'pending') {
    return 'pending_approval';
  }
  if (normalized === 'in_progress' || normalized === 'started') {
    return 'in_progress';
  }
  if (normalized === 'not_started') return 'not_started';

  return fallback || 'not_started';
}

function inferStatusFromRecord(
  raw: any,
  statusMapping: Record<string, JourneyMapCompletionStatus>,
): JourneyMapCompletionStatus {
  const directStatus =
    raw?.status ??
    raw?.completion_status ??
    raw?.state ??
    raw?.result ??
    raw?.approval_status;

  if (directStatus) {
    return normalizeStatus(directStatus, statusMapping);
  }

  if (asBoolean(raw?.approved)) return 'approved';
  if (asBoolean(raw?.completed)) return 'completed';
  if (asBoolean(raw?.is_complete)) return 'completed';
  if (asBoolean(raw?.passed)) return 'completed';
  if (asBoolean(raw?.failed)) return 'failed';
  if (asBoolean(raw?.blocked)) return 'blocked';

  const progress = clampPercent(raw?.progress ?? raw?.progress_percent ?? raw?.percent_complete);
  if (progress >= 100) return 'completed';
  if (progress > 0) return 'in_progress';

  return 'not_started';
}

function inferProgressFromRecord(raw: any, status: JourneyMapCompletionStatus): number {
  const directProgress =
    raw?.progress ??
    raw?.progress_percent ??
    raw?.percent_complete ??
    raw?.completion_percent;

  const parsed = asNumber(directProgress);
  if (parsed !== null) {
    return clampPercent(parsed);
  }

  if (status === 'approved' || status === 'completed') return 100;
  if (status === 'in_progress') return 50;
  return 0;
}

function getStatusRank(status: JourneyMapCompletionStatus): number {
  switch (status) {
    case 'blocked':
      return 700;
    case 'approved':
      return 650;
    case 'completed':
      return 600;
    case 'pending_approval':
      return 500;
    case 'in_progress':
      return 400;
    case 'failed':
      return 300;
    case 'not_started':
    default:
      return 100;
  }
}

function getSourceRank(
  source: JourneyMapCompletionSource,
  sourcePriority: JourneyMapCompletionSource[],
): number {
  const index = sourcePriority.indexOf(source);
  if (index === -1) return 0;
  return sourcePriority.length - index;
}

export function getDefaultThreshold(autoCompleteType: AutoCompleteType | null): number | null {
  if (autoCompleteType === 'course_completion') return 90;
  if (autoCompleteType === 'assessment_score') return 80;
  return null;
}

export function normalizeJourneyMapCompletionConfig(
  value?: Partial<JourneyMapCompletionConfigValue> | null,
): JourneyMapCompletionConfigValue {
  return {
    mode: value?.mode || DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG.mode,
    autoCompleteType:
      value?.autoCompleteType === undefined
        ? DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG.autoCompleteType
        : value.autoCompleteType,
    thresholdPercent:
      value?.thresholdPercent === undefined
        ? DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG.thresholdPercent
        : value.thresholdPercent,
    providerConfig: {
      ...DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG.providerConfig,
      ...(value?.providerConfig || {}),
      passStatuses: Array.isArray(value?.providerConfig?.passStatuses)
        ? value!.providerConfig!.passStatuses
        : DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG.providerConfig.passStatuses,
    },
  };
}

export function legacyConfigFromCompletionConfigValue(
  config?: Partial<JourneyMapCompletionConfigValue> | null,
): LegacyJourneyMapCompletionConfig {
  const normalized = normalizeJourneyMapCompletionConfig(config);

  const isManual = normalized.mode === 'manual';
  const isManager = normalized.mode === 'manager';
  const isAutoComplete = normalized.mode === 'auto_complete';
  const isAssessment = normalized.autoCompleteType === 'assessment_score';

  return {
    allowManualCompletion: isManual,
    requireManagerApproval: isManager,
    allowManagerApproval: isManager,
    autoCompleteFromProvider: isAutoComplete,
    passingScore:
      isAutoComplete && isAssessment ? normalized.thresholdPercent ?? 80 : null,
    sourcePriority: isManager
      ? ['manager', 'provider_api', 'assessment', 'manual', 'system', 'unknown']
      : isAutoComplete
        ? isAssessment
          ? ['assessment', 'provider_api', 'manager', 'manual', 'system', 'unknown']
          : ['provider_api', 'assessment', 'manager', 'manual', 'system', 'unknown']
        : ['manual', 'manager', 'provider_api', 'assessment', 'system', 'unknown'],
    providerMappings: {
      statusField:
        normalized.providerConfig.responseProcess === 'status_match'
          ? normalized.providerConfig.responseAttribute || 'status'
          : 'status',
      completedAtField: 'completed_at',
      progressField:
        normalized.autoCompleteType === 'course_completion'
          ? normalized.providerConfig.responseAttribute || 'progress'
          : 'progress',
      scoreField:
        normalized.autoCompleteType === 'assessment_score'
          ? normalized.providerConfig.responseAttribute || 'score'
          : 'score',
      attemptsField: 'attempts',
      approvedAtField: 'approved_at',
      approvedByField: 'approved_by',
    },
    statusMapping: {
      ...DEFAULT_LEGACY_JOURNEY_MAP_COMPLETION_CONFIG.statusMapping,
      ...(normalized.providerConfig.responseProcess === 'status_match'
        ? normalized.providerConfig.passStatuses.reduce(
          (acc, status) => {
            acc[String(status).trim().toLowerCase()] = 'completed';
            return acc;
          },
          {} as Record<string, JourneyMapCompletionStatus>,
        )
        : {}),
    },
  };
}

export function normalizeCompletionRecord(
  raw: any,
  config:
    | LegacyJourneyMapCompletionConfig
    | JourneyMapCompletionConfigValue = DEFAULT_LEGACY_JOURNEY_MAP_COMPLETION_CONFIG,
): NormalizedCompletionRecord {
  const legacyConfig =
    'mode' in (config as any)
      ? legacyConfigFromCompletionConfigValue(config as JourneyMapCompletionConfigValue)
      : (config as LegacyJourneyMapCompletionConfig);

  const inferredSource = normalizeSource(
    raw?.source ?? raw?.source_type ?? raw?.origin ?? raw?.updated_by_type,
  );

  const providerStatus =
    getByPath(raw, legacyConfig.providerMappings.statusField) ??
    raw?.status ??
    raw?.completion_status;

  const status = normalizeStatus(
    providerStatus,
    legacyConfig.statusMapping,
    inferStatusFromRecord(raw, legacyConfig.statusMapping),
  );

  const completedAt =
    asString(getByPath(raw, legacyConfig.providerMappings.completedAtField)) ??
    asString(raw?.completed_at) ??
    asString(raw?.completedAt) ??
    asString(raw?.completion_date);

  const approvedAt =
    asString(getByPath(raw, legacyConfig.providerMappings.approvedAtField)) ??
    asString(raw?.approved_at) ??
    asString(raw?.approvedAt);

  const approvedBy =
    asString(getByPath(raw, legacyConfig.providerMappings.approvedByField)) ??
    asString(raw?.approved_by) ??
    asString(raw?.approvedBy) ??
    asString(raw?.manager_name);

  const score =
    asNumber(getByPath(raw, legacyConfig.providerMappings.scoreField)) ??
    asNumber(raw?.score) ??
    asNumber(raw?.result_score);

  const attemptCount =
    asNumber(getByPath(raw, legacyConfig.providerMappings.attemptsField)) ??
    asNumber(raw?.attempts) ??
    asNumber(raw?.attempt_count);

  const progressPercent = inferProgressFromRecord(raw, status);

  return {
    source: inferredSource,
    status,
    progressPercent,
    completedAt,
    approvedAt,
    approvedBy,
    score,
    attemptCount,
    isManual: inferredSource === 'manual',
    isProviderManaged: inferredSource === 'provider_api',
    raw,
  };
}

export function resolveJourneyMapCompletion(
  rawRecords: any[],
  config:
    | LegacyJourneyMapCompletionConfig
    | JourneyMapCompletionConfigValue = DEFAULT_LEGACY_JOURNEY_MAP_COMPLETION_CONFIG,
): ResolvedJourneyMapCompletion {
  const legacyConfig =
    'mode' in (config as any)
      ? legacyConfigFromCompletionConfigValue(config as JourneyMapCompletionConfigValue)
      : (config as LegacyJourneyMapCompletionConfig);

  const records = (rawRecords || []).map((record) =>
    normalizeCompletionRecord(record, legacyConfig),
  );

  if (!records.length) {
    return {
      finalStatus: 'not_started',
      progressPercent: 0,
      winningSource: null,
      completedAt: null,
      approvedAt: null,
      approvedBy: null,
      score: null,
      attemptCount: null,
      records: [],
      canManuallyComplete: legacyConfig.allowManualCompletion,
      canRequestApproval: false,
      canApprove: legacyConfig.allowManagerApproval,
    };
  }

  const rankedRecords = records
    .slice()
    .sort((a, b) => {
      const statusDiff = getStatusRank(b.status) - getStatusRank(a.status);
      if (statusDiff !== 0) return statusDiff;

      const sourceDiff =
        getSourceRank(b.source, legacyConfig.sourcePriority) -
        getSourceRank(a.source, legacyConfig.sourcePriority);
      if (sourceDiff !== 0) return sourceDiff;

      return (b.progressPercent || 0) - (a.progressPercent || 0);
    });

  const winningRecord = rankedRecords[0];

  let finalStatus = winningRecord.status;

  if (
    legacyConfig.requireManagerApproval &&
    finalStatus === 'completed' &&
    !records.some((record) => record.status === 'approved')
  ) {
    finalStatus = 'pending_approval';
  }

  if (
    legacyConfig.passingScore !== null &&
    winningRecord.score !== null &&
    winningRecord.score < legacyConfig.passingScore &&
    finalStatus === 'completed'
  ) {
    finalStatus = 'failed';
  }

  const progressPercent = Math.max(...records.map((record) => record.progressPercent || 0), 0);

  return {
    finalStatus,
    progressPercent,
    winningSource: winningRecord.source,
    completedAt:
      records.find((record) => record.status === 'approved')?.completedAt ||
      winningRecord.completedAt,
    approvedAt:
      records.find((record) => record.status === 'approved')?.approvedAt || null,
    approvedBy:
      records.find((record) => record.status === 'approved')?.approvedBy || null,
    score: winningRecord.score,
    attemptCount: winningRecord.attemptCount,
    records,
    canManuallyComplete:
      legacyConfig.allowManualCompletion &&
      finalStatus !== 'approved' &&
      finalStatus !== 'completed',
    canRequestApproval:
      legacyConfig.requireManagerApproval &&
      (finalStatus === 'completed' || finalStatus === 'pending_approval'),
    canApprove:
      legacyConfig.allowManagerApproval &&
      (finalStatus === 'pending_approval' || finalStatus === 'completed'),
  };
}

export function evaluateAutoCompleteResponse(
  responseBody: any,
  config: JourneyMapCompletionConfigValue,
): {
  passed: boolean;
  actualValue: any;
  reason: string;
} {
  const normalized = normalizeJourneyMapCompletionConfig(config);
  const responseAttribute = normalized.providerConfig.responseAttribute;
  const process = normalized.providerConfig.responseProcess;
  const threshold = normalized.thresholdPercent;

  const actualValue = responseAttribute
    ? getByPath(responseBody, responseAttribute)
    : responseBody;

  if (process === 'boolean_true') {
    const passed = actualValue === true;
    return {
      passed,
      actualValue,
      reason: passed
        ? 'Response attribute evaluated to true.'
        : 'Response attribute did not evaluate to true.',
    };
  }

  if (process === 'status_match') {
    const statusValue = String(actualValue ?? '')
      .trim()
      .toLowerCase();
    const allowed = (normalized.providerConfig.passStatuses || []).map((status) =>
      String(status).trim().toLowerCase(),
    );
    const passed = allowed.includes(statusValue);

    return {
      passed,
      actualValue,
      reason: passed
        ? `Status matched one of: ${allowed.join(', ')}`
        : `Status did not match one of: ${allowed.join(', ')}`,
    };
  }

  const numericValue = Number(actualValue);
  const numericThreshold = Number(threshold ?? 0);
  const passed = !Number.isNaN(numericValue) && numericValue >= numericThreshold;

  return {
    passed,
    actualValue,
    reason: passed
      ? `Numeric value ${numericValue} met threshold ${numericThreshold}.`
      : `Numeric value ${String(actualValue)} did not meet threshold ${numericThreshold}.`,
  };
}